// A specialist's Portfolio page: one row per project it works, for any agent that
// keeps a status row.
// Mirrors inrange_agents/lambda_handlers/api/stateful_agents/portfolio/.
// See docs/Y2026-Q3/design-portfolio-daily-runs.md sections 5.1 and 5.4.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { NextMove } from "./useProjectAgentStatus";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";
import type { BuildingReadiness } from "./buildingReadiness";

export interface Counterparty {
  org: string;
  org_kind: string;
  person_name: string;
  person_email: string;
  reference: string;
}

export interface NextStepOwner {
  who: string;
  awaiting_what: string;
  since_date: string;
  // What moved the ball on since_date; shown on hover over the date.
  since_why: string;
  owner: string;
}

/** Whether this agent's side of the project is on course for the site's target. */
export interface OnTrack {
  verdict: "" | "on_track" | "at_risk" | "blocked";
  reason: string;
  by: string;
  // The action_id or date_id the date belongs to; "" on a verdict written before
  // 13 Oct 2026 or one that names none.
  item: string;
}

/** The action or tracked date a verdict's date belongs to: an action is what has to
 * close by the date, a tracked date is the deadline itself. */
export interface VerdictItem {
  kind: "action" | "date";
  title: string;
  /** A tracked date's own date; "" for an action, which is needed by the verdict's. */
  date: string;
}

// One application to a network operator, or one amendment of it (an entry of its
// own). Mirrors Application in inrange_agents/portfolio_row.py.
export interface Application {
  org: string;
  org_kind: string;
  references: string[];
  // ISO date it was submitted, or the amendment made.
  date: string;
  pv_installed_kw: number | null;
  battery_kw: number | null;
  export_kw: number | null;
  stage: string;
  // What an amendment changed; "" on an original application.
  changed: string;
  upload_id: string;
}

export interface PortfolioRow {
  lifecycle: string;
  next_step_owner: NextStepOwner;
  // Absent on rows stored before the verdict existed.
  on_track?: OnTrack;
  // The workbook's Status details cell.
  status_details: string;
  counterparties: Counterparty[];
  applications: Application[];
  options: string;
  // Building readiness's three areas; null on every other agent's row.
  readiness?: BuildingReadiness | null;
  // From the operator's workbook; cleared once an agent grounds the row itself.
  seeded: boolean;
}

export interface RowGlance {
  // Who owes the next move and for how long.
  line: string;
  // The status's first line.
  opener: string;
}

export interface PortfolioEntry {
  // Set when the LAST scheduled run failed on this site or never reached it
  // (section 5.4); absent when it was deliberately skipped.
  attention?: string | null;
  project_id: string;
  project_name: string;
  version: number;
  updated_at: string | null;
  set_by_run_id: string | null;
  // Null on a project enrolled but not yet worked.
  row: PortfolioRow | null;
  // The row at a glance, built when the page is read (portfolio_glance.py); null
  // with the row.
  glance: RowGlance | null;
  /** Who owes the next move, from this agent's blocking action. */
  next_move: NextMove;
  /** What this agent left waiting on the project (run_attention.waiting_for_agent). */
  waiting: { decisions: number; emails: number };
  /** The latest scheduled run that worked the project, where its open items are. */
  latest_run_id: string | null;
}

// One agent's row values (RowVocabulary in inrange_agents/portfolio_row.py):
// its stages in order of progress, and who can owe the next move.
export interface RowVocabulary {
  lifecycle: string[];
  owners: string[];
}

export interface PortfolioList {
  agent: string;
  label: string;
  // Whether this agent's rows keep an Applications box.
  keeps_applications: boolean;
  vocabulary: RowVocabulary;
  enrolled: PortfolioEntry[];
  // Rows nothing maintains, listed apart so their staleness stays visible.
  unenrolled: PortfolioEntry[];
}


export const useAgentPortfolio = (agent: string | null) => {
  const http = useAgentsHttp();
  return useQuery<PortfolioList>(
    [QK.agentPortfolio, agent],
    () => http<PortfolioList>("/stateful-agents/portfolio/list", { agent }),
    { enabled: !!agent },
  );
};

export const useSetEnrolment = (agent: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { changed: boolean },
    Error,
    { project_id: string; enrolled: boolean; actor_email: string }
  >(
    (body) =>
      http<{ changed: boolean }>("/stateful-agents/portfolio/enrol", {
        agent,
        ...body,
      }),
    {
      // Awaited, so the checkbox stays "Saving…" until it shows the saved state.
      onSuccess: (_data, { project_id }) =>
        Promise.all([
          qc.invalidateQueries([QK.agentPortfolio, agent]),
          qc.invalidateQueries([QK.teamSettings]),
          qc.invalidateQueries([QK.projectEnrolment, project_id]),
        ]),
    },
  );
};

export interface TeamSettingsProject {
  project_id: string;
  project_name: string;
  scheduled_runs: boolean;
  // What its first run would cost once its scheduled runs are on; null while they are.
  runs_on_usd: number | null;
  automated_review: boolean;
  sppa_clause_lists: boolean;
  client_spec_clause_lists: boolean;
  master_agreement_clause_lists: boolean;
}

export interface SpecialistOnProject {
  project_id: string;
  enrolled: boolean;
  // What its first pass here would likely cost, in USD; null while it is on.
  first_pass_usd: number | null;
}

// What switching on costs where it does not depend on the project, in USD rounded to $5.
export interface SwitchCosts {
  automated_review_nightly: number;
  // A caught-up specialist's day on a project with activity.
  specialist_daily_low: number;
  specialist_daily_high: number;
  // Extracting a document's clauses: once per client specification or master agreement
  // for every site it binds, once per signed version of a project's SPPA.
  client_spec_extract: number;
  master_agreement_extract: number;
  sppa_extract: number;
  // Assessing a project against a clause list: its first run, then at most this a day.
  clause_first_assess: number;
  clause_assess_daily_max: number;
}

// Every project, with each per-project setting.
export interface TeamSettings {
  always_on: AlwaysOnAgent[];
  switch_costs: SwitchCosts;
  all_projects: TeamSettingsProject[];
  specialists: {
    agent: string;
    label: string;
    describe: string;
    all_projects: SpecialistOnProject[];
  }[];
}

export const useTeamSettings = () => {
  const http = useAgentsHttp();
  return useQuery<TeamSettings>([QK.teamSettings], () =>
    http<TeamSettings>("/stateful-agents/portfolio/team-settings", {}),
  );
};

export interface ManifestDocument {
  upload_id: string;
  name: string;
  kind: string;
  state: "NEW" | "UPDATED";
  state_reason: string;
  arrived_at: string;
  // Both or neither can be true, so they are not collapsed to one flag.
  sent_by_us: boolean;
  received: boolean;
  // Where a partly-read thread resumes from.
  after_message_id: string | null;
  // Where it was seen, rendered by the same code as the agent's brief.
  seen_at: string[];
}

// A clock on a site's row whose state changed since the agent last reviewed the
// site (approaching, overdue, due, quiet). It is why a quiet site is worked.
export interface ClockChange {
  project_id: string;
  project_name: string;
  /** Whose row the clock is on: the team's plan holds every specialist's. */
  agent: string;
  label: string;
  description: string;
}

export interface DayManifest {
  next_run_at?: string;
  agent: string;
  /** New or updated uploads on the sites themselves - never global files. */
  document_count: number;
  /** Global files not yet read on some site, each counted once however many sites
   * list it. One works a site only when it changed since an agent's last pass there. */
  global_file_count: number;
  projects_to_work: string[];
  projects: {
    project_id: string;
    project_name: string;
    moved: boolean;
    /** Why the pass works this site - the plan's own answer - or null when it does not. */
    work_reason:
      | "chosen"
      | "arrivals"
      | "slack"
      | "global"
      | "removed"
      | "document_clauses"
      | "first_pass"
      | "clocks"
      | "handed"
      | "reviewed"
      | "retest"
      | null;
    /** Agents on their first pass here, and how many documents they will triage:
     * new to them because they have never worked the site, not because anything came. */
    first_pass: { agent: string; label: string; documents: number }[];
    /** New on the site itself. */
    documents: ManifestDocument[];
  }[];
  clock_changes: ClockChange[];
}

// What today's sweep would work, readable BEFORE it runs.
export const useDayManifest = (agent: string | null) => {
  const http = useAgentsHttp();
  return useQuery<DayManifest>(
    [QK.dayManifest, agent],
    () => http<DayManifest>("/stateful-agents/portfolio/manifest", { agent }),
    { enabled: !!agent },
  );
};

export interface AgentRun {
  run_id: string;
  project_id: string;
  agent: string | null;
  kind: string;
  title: string;
  status: string;
  message_count: number;
  projects_worked: number;
  // null when no ledger row was found: "not recorded" is not "free".
  spend_usd: number | null;
  /** The unsent emails this run leaves (run_attention). */
  waiting?: { emails: number };
  context_label?: string;
  // Never the sweep sentinel, so the client cannot link to a non-project.
  context_project_id?: string | null;
  // Who opened it, and everyone who has since sent a message (oldest first).
  started_by_email?: string;
  participants?: string[];
  // Which AGENTS worked in the run, first-acted first - not the same question
  // as `participants`, which is the people in it.
  agents_in_run?: string[];
  // The sites this run is about, by name and sorted; empty means any site.
  projects?: string[];
  // Hidden from everyone but its owner; only the owner may toggle it.
  is_private?: boolean;
  is_owner?: boolean;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  // When a scheduled pass finished; kept when the run is re-opened.
  pass_ended_at?: string | null;
  // A scheduled pass still being worked: it holds no turn pointer.
  pass_running?: boolean;
}

/** A running pass a queued "Run now" waits on. `projects` is empty for one over every site. */
export interface BlockingRun {
  run_id: string;
  projects: string[];
  started_by_email: string;
  created_at: string;
}

/** Start the team's pass now. `project_ids` names the sites it covers - which
 * also overrides the plan's own selection, so a named site is worked whether or
 * not anything arrived on it. `prompt` is the operator's note for the run, read
 * on every project it works. */
// ``run_id`` is set only when the run was staged ("started"); ``waiting_on`` only when
// it was queued behind other runs ("queued").
interface RunNowResult {
  status: string;
  detail: string;
  run_id?: string | null;
  waiting_on?: BlockingRun[];
}


/** A site's refresh still waiting to start, so a reloaded page still says so. Polled
 * while it waits, which is how the page learns it has started. */
export interface QueuedPass {
  queued: boolean;
  requested_at: string | null;
  waiting_on: BlockingRun[];
  /** The latest request the queue refused for this site and slot, and why. */
  refused?: { reason: string; at: string } | null;
}

export const useQueuedPass = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<QueuedPass>(
    [QK.queuedPass, projectId],
    () => http("/stateful-agents/sweep/queued", { project_id: projectId }),
    { refetchInterval: (data) => (data?.queued ? 30_000 : false) },
  );
};

export const useRunSweepNow = (agent: string | null) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    RunNowResult,
    Error,
    {
      prompt?: string;
      project_ids?: string[];
      actor_email: string;
      /** The page the button is on ("Legal"), shown on the run's page. */
      started_from: string;
      /** Run the automated reviewer on sites that have it on. */
      automated_review: boolean;
      /** The agent slot to run, for a run on one project; absent means default. */
      config?: string;
    }
  >(
    (vars) =>
      http<RunNowResult>("/stateful-agents/sweep/run-now", {
        agent,
        actor_email: vars.actor_email,
        started_from: vars.started_from,
        automated_review: vars.automated_review,
        ...(vars.prompt ? { prompt: vars.prompt } : {}),
        // Omitted, not sent empty: absent means "whatever the plan finds", and a
        // list means "work exactly these, moved or not".
        ...(vars.project_ids?.length ? { project_ids: vars.project_ids } : {}),
        ...(vars.config ? { config: vars.config } : {}),
      }),
    {
      onSuccess: () => {
        void qc.invalidateQueries([QK.agentRuns]);
        void qc.invalidateQueries([QK.dayManifest]);
        // The per-project Scheduled tab reads a different key, and its whole
        // point is that the run it just started shows up in the list below.
        void qc.invalidateQueries([QK.statefulRuns]);
        void qc.invalidateQueries([QK.queuedPass]);
      },
    },
  );
};

export const useAgentRuns = (
  // Several agents: the project team's tabs, in one request.
  agent: string | string[] | null,
  kind: "scheduled" | "chat" | "agent_chat" | "learning_review" | "conversations",
  // Applied SERVER-side: filtering a capped list here drops a quiet agent's older runs.
  range?: { from: string; to: string },
  // Conversation types and projects to narrow to; empty means all.
  filters: { types?: string[]; projectIds?: string[] } = {},
) => {
  const http = useAgentsHttp();
  return useQuery<{ runs: AgentRun[] }>(
    [QK.agentRuns, agent, kind, range?.from, range?.to, filters],
    () =>
      http<{ runs: AgentRun[] }>("/stateful-runs/list-for-agent", {
        ...(Array.isArray(agent) ? { agents: agent } : { agent }),
        kind,
        ...(range ? { from: range.from, to: range.to } : {}),
        // The server bounds days in this zone, so they are the operator's days.
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(filters.types?.length ? { types: filters.types } : {}),
        ...(filters.projectIds?.length
          ? { project_ids: filters.projectIds }
          : {}),
      }),
    {
      enabled: !!agent,
      // Polled only while a pass is running, so its "so far" run time moves.
      refetchInterval: (data) =>
        data?.runs.some((run) => run.pass_running) ? 15_000 : false,
    },
  );
};

// Who works this project. One boolean for whether a scheduled run comes here at
// all (it moves the always-on agents together), plus one switch per optional
// specialist - those are a real per-site choice, and the settings card is the
// only surface that sets them.
export interface AlwaysOnAgent {
  agent: string;
  label: string;
}

export interface ProjectSpecialist {
  agent: string;
  label: string;
  describe: string;
  // Switched on per project; an always-on one works every project whose runs are on.
  optional: boolean;
  enrolled: boolean;
  // What its first pass here would likely cost, in USD rounded to $5; null unless it is off.
  first_pass_usd: number | null;
}

export interface ProjectLearningAgents {
  project_id: string;
  enabled: boolean;
  // What its first run would cost once its scheduled runs are on; null while they are.
  runs_on_usd: number | null;
  // The agents that move with the project-level switch and carry no control of
  // their own. Named by the backend rather than restated in UI copy: a roster
  // written out by hand is one somebody has to keep in step.
  always_on: AlwaysOnAgent[];
  // Every specialist, in team order.
  specialists: ProjectSpecialist[];
  // The automated reviewer checks this project at the end of each scheduled pass.
  automated_review: boolean;
  switch_costs: SwitchCosts;
  // This project uses the SPPA's / the client building spec's / the master agreement's
  // clause list.
  sppa_clause_lists: boolean;
  client_spec_clause_lists: boolean;
  master_agreement_clause_lists: boolean;
}


export const useProjectLearningAgents = (projectId: string) => {
  const http = useAgentsHttp();
  return useQuery<ProjectLearningAgents>(
    [QK.projectEnrolment, projectId],
    () =>
      http<ProjectLearningAgents>(
        "/stateful-agents/portfolio/project-enrolment",
        { project_id: projectId },
      ),
    { enabled: Boolean(projectId) },
  );
};

export const useSetProjectLearningAgents = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { enabled: boolean; changed: number },
    Error,
    { project_id: string; enabled: boolean; actor_email: string }
  >(
    (body) =>
      http<{ enabled: boolean; changed: number }>(
        "/stateful-agents/portfolio/project-enrolment/set",
        body,
      ),
    {
      // Awaited, so the checkbox stays busy until it shows the saved state. The Portfolio
      // tab reads the same enrolment from the agent side.
      onSuccess: (_data, { project_id }) =>
        Promise.all([
          qc.invalidateQueries([QK.projectEnrolment, project_id]),
          qc.invalidateQueries([QK.agentPortfolio]),
          qc.invalidateQueries([QK.teamSettings]),
        ]),
    },
  );
};

// A project switch held in one project column. The paths are the routes'; a clause list
// switched off vanishes from the project's agents and pages, so its registers refetch.
export type ProjectSwitch =
  | "automated_review"
  | "sppa_clause_lists"
  | "client_spec_clause_lists"
  | "master_agreement_clause_lists";

const SWITCH_ROUTES: Record<ProjectSwitch, string> = {
  automated_review: "/stateful-agents/portfolio/project-automated-review/set",
  sppa_clause_lists: "/stateful-agents/portfolio/project-sppa-clause-lists/set",
  client_spec_clause_lists:
    "/stateful-agents/portfolio/project-client-spec-clause-lists/set",
  master_agreement_clause_lists:
    "/stateful-agents/portfolio/project-master-agreement-clause-lists/set",
};

export const useSetProjectSwitch = (column: ProjectSwitch) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { enabled: boolean; changed: boolean },
    Error,
    { project_id: string; enabled: boolean }
  >((body) => http(SWITCH_ROUTES[column], body), {
    // Awaited, so the checkbox stays busy until it shows the saved state.
    onSuccess: (_data, { project_id }) =>
      Promise.all([
        qc.invalidateQueries([QK.projectEnrolment, project_id]),
        qc.invalidateQueries([QK.teamSettings]),
        qc.invalidateQueries([QK.projectRegisters, project_id]),
      ]),
  });
};

// One optional specialist, on or off for one project. Shares the per-agent enrol
// route with the Portfolio tab; the route refuses a non-optional agent, so this
// can only ever move one of the three.
export const useSetProjectSpecialist = (projectId: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { changed: boolean },
    Error,
    { agent: string; enrolled: boolean; actor_email: string }
  >(
    (body) =>
      http<{ changed: boolean }>("/stateful-agents/portfolio/enrol", {
        project_id: projectId,
        ...body,
      }),
    {
      onSuccess: () =>
        Promise.all([
          qc.invalidateQueries([QK.projectEnrolment, projectId]),
          qc.invalidateQueries([QK.agentPortfolio]),
          qc.invalidateQueries([QK.teamSettings]),
        ]),
    },
  );
};

// The per-project state documents one agent maintains, asked from the AGENT.
export interface AgentStateDocumentRow {
  project_id: string;
  name: string;
  version: number;
  updated_at: string | null;
  chars: number;
}

export const useAgentStateDocuments = (agent: string | null) => {
  const http = useAgentsHttp();
  return useQuery<{ agent: string; documents: AgentStateDocumentRow[] }>(
    [QK.agentStateDocuments, agent],
    () =>
      http<{ agent: string; documents: AgentStateDocumentRow[] }>(
        "/stateful-agents/state/documents",
        { agent },
      ),
    { enabled: !!agent },
  );
};
